import { createContext, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { vehicleService } from '../../../shared/services/vehicles/vehiclesServices';
import { useNavigate, useParams } from 'react-router-dom';

export interface Vehicle {
  id: string;
  marca: string;
  modelo: string;
  tipo: 'carro' | 'moto' | 'caminhao' | 'outro';
  placa: string;
  ano: number;
  cor: string;
  observacoes?: string;
  foto?: string;
  tenantId: string;
  tenantName?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

interface VehicleContextData {
  // Listagem
  vehicles: Vehicle[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (vehicle: Vehicle) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedVehicle: Vehicle | null;
  openDeleteModal: (vehicle: Vehicle) => void;
  closeDeleteModal: () => void;
  handleDeleteVehicle: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  vehicleData: Vehicle | null;
  isDataLoading: boolean;
  createVehicle: (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateVehicle: (id: string, data: Partial<Vehicle>) => Promise<void>;
  isSubmitting: boolean;
  getVehicleById: (id: string) => Vehicle;

  setError: (error: string | null) => void;
}

interface VehicleProviderProps {
  children: ReactNode;
}

export const VehicleContext = createContext({} as VehicleContextData);

export function VehicleProvider({ children }: VehicleProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Query para listar veículos
  const { data: vehicles = [], isLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      try {
        const response = await vehicleService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar veículos';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar veículos');
        }
        return [];
      }
    }
  });

  // Query para buscar dados do veículo no modo de edição
  const { data: vehicleData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await vehicleService.getById(id);
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar veículo';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar veículo');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Mutation para deletar veículo
  const deleteMutation = useMutation({
    mutationFn: (id: string) => vehicleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      navigate('/vehicles');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/vehicles/new');
  };

  const handleView = (vehicle: Vehicle) => {
    navigate(`/vehicles/${vehicle.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedVehicle(null);
  };

  const handleDeleteVehicle = () => {
    if (selectedVehicle?.id) {
      deleteMutation.mutate(selectedVehicle.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/vehicles/${id}/edit`);
  };

  // Mutations para criar e atualizar veículos
  const createMutation = useMutation({
    mutationFn: (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => vehicleService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar veículo';
        setError(errorMessage);
      } else {
        setError('Erro ao criar veículo');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Vehicle> }) => vehicleService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar veículo';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar veículo');
      }
    }
  });

  const createVehicle = async (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateVehicle = async (id: string, data: Partial<Vehicle>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getVehicleById = (id: string) => {
    return vehicles.find(vehicle => vehicle.id === id) || {} as Vehicle;
  };

  return (
    <VehicleContext.Provider
      value={{
        // Listagem
        vehicles,
        isLoading,
        loading: isLoading,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedVehicle,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteVehicle,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        vehicleData,
        isDataLoading,
        createVehicle,
        updateVehicle,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getVehicleById,

        setError,
      }}
    >
      {children}
    </VehicleContext.Provider>
  );
} 